import React, { useEffect, useMemo } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '../../../components/atoms/Button';
import { ButtonKind } from '../../../components/atoms/Button/enums';
import { FormRadioList } from '../../../components/atoms/Fields/Radio';
import renderDefaultRadioItem from '../../../components/atoms/Fields/Radio/content/default';
import ScCard from '../../../components/atoms/ScCard';
import ScSectionHeader from '../../../components/molecules/ScSectionHeader';
import useHeaderTitleContext from '../../../hooks/useHeaderTitleContext';
import useUserContext from '../../../hooks/useUserContext';
import {
  PreferredCurrency,
  PreferredPayment,
} from '../../../setup/apiTypes/enums';
import { currencyRadioItems, paymentMethodRadioItems } from './consts';
import {
  useSetPreferredPaymentMethodSubmitHandler,
  useSetPreferredCurrencySubmitHandler,
} from './hooks';
import { StyledRadioListWrapper } from './styles';
import {
  PreferredCurrencyFormValues,
  PreferredPaymentFormValues,
} from './types';

const BillingAndPaymentsPage: React.FC = () => {
  const { user } = useUserContext();
  const { setHeaderTitle } = useHeaderTitleContext();
  const intl = useIntl();
  const paymentMethodItems = useMemo(() => paymentMethodRadioItems(intl), [intl]);
  const setPreferredPaymentMethodSubmitHandler = useSetPreferredPaymentMethodSubmitHandler();
  const setPreferredCurrencySubmitHandler = useSetPreferredCurrencySubmitHandler();

  useEffect(() => {
    setHeaderTitle(
      intl.formatMessage({
        id: 'sidebar.settings.billingAndPayments',
        defaultMessage: 'Billing & Payments',
      })
    );
  }, [intl, setHeaderTitle]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-4)' }}>
      {/* Payment method card */}
      <ScCard style={{ padding: 'var(--s-6)' }}>
        <Form<PreferredPaymentFormValues>
          onSubmit={setPreferredPaymentMethodSubmitHandler}
          initialValues={
            user
              ? {
                  preferredPayment:
                    user.billingAndPayment.preferredPayment || undefined,
                }
              : undefined
          }
        >
          {({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              <ScSectionHeader
                title={intl.formatMessage({
                  id: 'billingAndPayments.selectYourPreferredPaymentMethod',
                  defaultMessage: 'Preferred payment method',
                })}
                subtitle={intl.formatMessage({
                  id: 'billingAndPayments.paymentMethodSubtitle',
                  defaultMessage: "How we'll send you your COD collections.",
                })}
              />
              <StyledRadioListWrapper style={{ maxWidth: 680 }}>
                <FormRadioList<PreferredPayment>
                  disabled={submitting}
                  name='preferredPayment'
                  items={paymentMethodItems}
                  renderRadioContent={renderDefaultRadioItem}
                />
              </StyledRadioListWrapper>
              <div style={{ display: 'flex', gap: 8, marginTop: 'var(--s-5)' }}>
                <Button type='submit' disabled={submitting}>
                  <FormattedMessage id='forms.buttons.save' defaultMessage='Save changes' />
                </Button>
                <Button type='reset' kind={ButtonKind.Ghost}>
                  <FormattedMessage id='forms.buttons.discard' defaultMessage='Discard' />
                </Button>
              </div>
            </form>
          )}
        </Form>
      </ScCard>

      {/* Currency card */}
      <ScCard style={{ padding: 'var(--s-6)' }}>
        <Form<PreferredCurrencyFormValues>
          onSubmit={setPreferredCurrencySubmitHandler}
          initialValues={
            user
              ? {
                  preferredCurrency:
                    user.billingAndPayment.preferredCurrency || undefined,
                }
              : undefined
          }
        >
          {({ handleSubmit, submitting }) => (
            <form onSubmit={handleSubmit}>
              <ScSectionHeader
                title={intl.formatMessage({
                  id: 'billingAndPayments.selectYourPreferredCurrency',
                  defaultMessage: 'Preferred currency',
                })}
              />
              <StyledRadioListWrapper style={{ maxWidth: 680 }}>
                <FormRadioList<PreferredCurrency>
                  disabled={submitting}
                  name='preferredCurrency'
                  items={currencyRadioItems}
                  renderRadioContent={renderDefaultRadioItem}
                />
              </StyledRadioListWrapper>
              <div style={{ display: 'flex', gap: 8, marginTop: 'var(--s-5)' }}>
                <Button type='submit' disabled={submitting}>
                  <FormattedMessage id='forms.buttons.save' defaultMessage='Save changes' />
                </Button>
                <Button type='reset' kind={ButtonKind.Ghost}>
                  <FormattedMessage id='forms.buttons.discard' defaultMessage='Discard' />
                </Button>
              </div>
            </form>
          )}
        </Form>
      </ScCard>
    </div>
  );
};

export default BillingAndPaymentsPage;
